import Vue from 'vue';
import { getDomain } from '@/store/auth/auth.store';
import { AccessTokenKey, AUTH_STATE_COOKIE } from '@/store/auth/auth.store.constants';

// Helper function to generate CSRF token
function generateRandomToken(length = 32) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export const authHandler = {
  methods: {
    /**
     * Sets the authentication token and handles redirection
     * @param {string} token - The authentication token
     * @param {number} expiryDays - Number of days until token expires (default: 7)
     * Used in Login, LoginSSO and Register views
     */
    async handleAuthSuccess(token, expiryDays = 7) {
      // console.log('handleAuthSuccess() START');
      try {
        // Validate token format
        if (!token || token.split('.').length !== 3) {
          throw new Error('Invalid JWT format');
        }

        // Set cookie expiry
        const date = new Date();
        date.setDate(date.getDate() + expiryDays);

        const isProduction = process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging';

        // Get parent domain for cross-subdomain sharing
        const parentDomain = getDomain();
        const domain = parentDomain ? `.${parentDomain}` : (process.env.VUE_APP_DOMAIN_1 || 'localhost');
        
        // Remove any 'app.' prefix from the domain if it exists
        const cleanDomain = domain.startsWith('.app.') ? domain.replace('.app.', '.') : domain;

        // Base cookie options
        const baseOptions = {
            path: '/',
            domain: cleanDomain,
            expires: date,
            secure: true,
            sameSite: 'Lax',  // Must be Lax for cross-origin requests
            priority: 'High'
        };

        console.log('Setting cookies with cross-origin compatible options:', JSON.stringify(baseOptions));

        // Set cookies with explicit options for each one
        const setCookie = (name, value, additionalOptions = {}) => {
            const options = {
                ...baseOptions,
                ...additionalOptions
            };
            
            // Force critical security options while maintaining cross-origin compatibility
            options.secure = true;  // Always use HTTPS
            options.sameSite = 'Lax';  // Required for cross-origin
            options.priority = 'High';
            
            Vue.$cookies.set(name, value, options);
            
            // Verify cookie was set
            const cookie = Vue.$cookies.get(name);
            if (!cookie) {
                console.error(`Failed to set cookie: ${name}`);
            }
        };

        // Store the complete token with specific options
        setCookie(AccessTokenKey, token, {
            httpOnly: false // We need JS access for API calls
        });

        // Set auth state cookie
        setCookie(AUTH_STATE_COOKIE, 'authenticated', {
            httpOnly: false
        });

        // Set CSRF token - this is especially important for cross-origin requests
        const csrfToken = generateRandomToken();
        setCookie('XSRF-TOKEN', csrfToken, {
            httpOnly: false
        });

        // Log successful cookie setup
        console.log('All cookies set successfully with cross-origin settings');

        // Handle redirection based on community type
        await this.handleRedirect();
      } catch (error) {
        console.error('Error in handleAuthSuccess:', error);
        throw error;
      }
    },

    /**
     * Handles redirection after successful authentication
     */
    async handleRedirect() {
      if (this.isIntercontinentalAlliance()) {
        window.location.href = "https://app.intercontinentalalliance.com/intercontinental-alliance";
        return;
      }

      const vueAppUrl = process.env.VUE_APP_APP_URL_1 || 'http://localhost:8080';
      let destinationPath = '';
      
      if (this.communitySlug) {
        const validSlug = typeof this.communitySlug === 'object' ? 
          (this.communitySlug.toString() || '') : 
          this.communitySlug;
        destinationPath = `/${validSlug}`;
      }

      const destinationURL = this.getValidPath(vueAppUrl + destinationPath);
      window.location.href = destinationURL;
    },

    /**
     * Ensures path is valid and safe
     */
    getValidPath(path) {
      if (typeof path === 'object') {
        console.error('Invalid path object:', path);
        return '/';
      }
      return path;
    }
  }
}; 